import React, {useEffect} from 'react'

import {Box, CircularProgress} from '@mui/material'

import {useConsultationsProgramDetailQuery} from '@progyny/generated/graphql'
import {MENOPAUSE_PROGRAM_SLUG} from '@progyny/pages/Consultations/Menopause/useMenopauseConsultationAppointments'
import EmotionalWellBeingDisclaimerModal from '@progyny/pages/Consultations/Program/components/EmotionalWellBeingDisclaimerModal'
import ProgramOverviewCard from '@progyny/pages/Consultations/Program/components/ProgramOverviewCard'
import {useProgramEnrollment} from '@progyny/pages/Consultations/hooks/useProgramEnrollment'

interface MenopauseOverviewProps {
  colors: any
}

const MenopauseOverview: React.FC<MenopauseOverviewProps> = ({colors}) => {
  const {data, loading, error, refetch} = useConsultationsProgramDetailQuery({
    variables: {slug: MENOPAUSE_PROGRAM_SLUG},
  })

  const program = data?.consultations?.program
  const programEnrollment = data?.consultations?.myProgramEnrollments?.find(
    (e) => e?.program?.id === program?.id
  )
  const isEnrolled = !!programEnrollment

  const {
    enrolling,
    requiresDisclaimer,
    showDisclaimerModal,
    setShowDisclaimerModal,
    handleEnroll,
    handleDisclaimerAcknowledge,
    handleDisclaimerClose,
  } = useProgramEnrollment({
    programId: program?.id ?? undefined,
    programSlug: MENOPAUSE_PROGRAM_SLUG,
    isEnrolled,
    onSuccess: () => refetch(),
  })

  const sessions =
    program?.sessions?.map((session) => {
      const sessionStatus = programEnrollment?.sessionStatuses?.find(
        (ss) => ss?.session?.id === session?.id
      )
      const appointment = sessionStatus?.appointment
      const status = sessionStatus?.status

      let displayStatus:
        | 'completed'
        | 'upcoming'
        | 'readyForBooking'
        | 'locked'
        | 'notAttended'
        | 'skipped'
      if (status === 'COMPLETED') {
        displayStatus = 'completed'
      } else if (status === 'BOOKED') {
        displayStatus = 'upcoming'
      } else if (status === 'AVAILABLE') {
        displayStatus = 'readyForBooking'
      } else if (status === 'SKIPPED') {
        displayStatus = 'skipped'
      } else {
        displayStatus = 'locked'
      }

      return {
        sessionIndex: String(session?.sessionNumber || ''),
        title: session?.title!,
        subtitle: session?.subtitle || '',
        description: session?.description!,
        status: displayStatus,
        appointment: appointment,
        required: session?.required ?? true,
      }
    }) || []

  useEffect(() => {
    if (!loading && data?.consultations?.program && requiresDisclaimer) {
      const prog = data.consultations.program
      const enrolled = data.consultations.myProgramEnrollments?.find(
        (e) => e?.program?.id === prog.id
      )
      if (!enrolled) {
        setShowDisclaimerModal(true)
      }
    }
  }, [loading, data, requiresDisclaimer, setShowDisclaimerModal])

  if (loading) {
    return (
      <Box
        minHeight={140}
        display='flex'
        alignItems='center'
        justifyContent='center'
      >
        <CircularProgress color='primary' size={32} />
      </Box>
    )
  }

  if (error || !program) {
    return null
  }

  const canEnrollInPrograms =
    program?.consultation?.canEnrollInPrograms ?? false
  const showBookingButton =
    isEnrolled && program?.bookingStrategy !== 'PRE_BOOK_ALL'
  const enableLearnMore = program?.enableLearnMoreContent ?? true

  return (
    <>
      <ProgramOverviewCard
        programSlug={MENOPAUSE_PROGRAM_SLUG}
        programDescription={program.description}
        bookingStrategy={program.bookingStrategy}
        isEnrolled={isEnrolled}
        canEnrollInPrograms={canEnrollInPrograms}
        enableLearnMore={enableLearnMore}
        showBookingButton={showBookingButton}
        sessions={sessions}
        colors={colors}
        enrolling={enrolling}
        onEnroll={handleEnroll}
      />
      {requiresDisclaimer && (
        <EmotionalWellBeingDisclaimerModal
          open={showDisclaimerModal}
          onClose={handleDisclaimerClose}
          onAcknowledge={handleDisclaimerAcknowledge}
          loading={enrolling}
        />
      )}
    </>
  )
}

export default MenopauseOverview
